import { useTranslation } from 'react-i18next'
import {
  CurecallButton,
  CurecallInput,
  CurecallSelect,
  CurecallSelectItem,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TContent, EContentType } from './content.type'
import { ProjectService } from 'src/services/project.service'
import { ContentEditor } from './content-editor-field.component'
import { ExternalLinkField } from './external-link-field.component'
import { useQueryClient } from '@tanstack/react-query'
import { ENABLED_PATIENT_LOCALES, TPatientLocale } from 'src/types/locales'
import { CONFLICT_ERROR_CODE } from 'src/utils/http.util'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useLocale } from 'src/hooks/use-locale'

export type TAddContentFormValues = Omit<TContent, 'id' | 'origin'>
export type TUpdateContentFormValues = Omit<TContent, 'origin'>

type TLanguageOption = CurecallSelectItem<TPatientLocale>

function ContentForm({
  contentType,
  isButtonLoading,
}: {
  contentType: EContentType
  isButtonLoading: boolean
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useFormContext<TAddContentFormValues>()
  const navigate = useNavigate()

  const languagesOptions = ENABLED_PATIENT_LOCALES.map((language: TPatientLocale) => {
    const isContentOfThisLanguageFilled = watch('content') && watch('content')[language]?.length
    return {
      label: t('common.languages', { language }),
      value: language,
      extraLabel: isContentOfThisLanguageFilled ? undefined : (
        <p className="c-ml-2 c-text-grey-500 c-italic c-m-0">
          {t('pages.configuration.contents.no-content')}
        </p>
      ),
    }
  })

  const [selectedLanguage, setSelectedLanguage] = useState<TLanguageOption | undefined>(
    languagesOptions.find((option) => option.value === locale),
  )

  const currentContentName = useMemo(
    () => `content.${selectedLanguage?.value}`,
    [selectedLanguage?.value],
  ) as 'content.fr' | 'content.en'

  useEffect(() => {
    const existingContent = getValues('content')
    if (existingContent) {
      let contentLanguage = locale as TPatientLocale
      if (!existingContent[locale as TPatientLocale]) {
        for (const language of ENABLED_PATIENT_LOCALES) {
          if (existingContent[language]) {
            contentLanguage = language as TPatientLocale
            break
          }
        }
      }
      const languageFilled = languagesOptions.find((option) => option.value === contentLanguage)
      setSelectedLanguage(languageFilled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeLocale = (option: TLanguageOption) => {
    setSelectedLanguage(option)
  }
  const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value
    clearErrors('name')
    setValue('name', newName)
  }

  return (
    <>
      <div className="c-flex c-w-full c-gap-10">
        <CurecallSelect
          selectedItem={selectedLanguage}
          onChange={onChangeLocale}
          label={t('pages.configuration.contents.content-language')}
          items={languagesOptions}
          tooltipPosition="right"
          tooltipContent={t('pages.configuration.contents.content-language-tooltip')}
          isRequired={true}
          wrapperClassName="c-grow"
        />

        <CurecallInput
          {...register('name', { required: t('pages.configuration.contents.content-name-required') })}
          onChange={onChangeName}
          label={t('pages.configuration.contents.content-name')}
          name="name"
          placeholder={t('pages.configuration.contents.content-name-placeholder')}
          tooltipContent={t('pages.configuration.contents.content-name-tooltip')}
          tooltipPosition="right"
          errorMessage={errors?.name?.message}
          isRequired={true}
          wrapperClassName="c-grow"
        />
      </div>

      {contentType === EContentType.CONTENT && <ContentEditor currentContentName={currentContentName} />}
      {contentType === EContentType.EXTERNAL_LINK && (
        <ExternalLinkField currentContentName={currentContentName} />
      )}

      <div className="c-mt-16 c-mb-8 c-flex c-justify-center c-gap-6">
        <CurecallButton
          appearance={ECurecallButtonAppearance.CANCEL}
          type="button"
          isLoading={isButtonLoading}
          onClick={() => navigate(-1)}
        >
          {t('common.actions.cancel')}
        </CurecallButton>
        <CurecallButton type="submit" isLoading={isButtonLoading}>
          {contentType === EContentType.EXTERNAL_LINK && t('pages.configuration.contents.save-external-link')}
          {contentType === EContentType.CONTENT && t('pages.configuration.contents.save-content')}
        </CurecallButton>
      </div>
    </>
  )
}

function AddContentForm({ contentType }: { contentType: EContentType }) {
  const { t } = useTranslation()
  const methods = useForm<TAddContentFormValues>()
  const { project } = useCurrentProject()
  const { handleSubmit, setError } = methods
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const onSubmit = async (formData: TAddContentFormValues) => {
    if (!formData.content) {
      setError('content', { type: 'custom', message: t('pages.configuration.contents.content-required') })
      return
    }
    setIsButtonLoading(true)
    try {
      const result = await ProjectService.postSelfConfigurationContent(project.id, {
        ...formData,
        type: contentType,
      })
      if (queryClient.getQueryData(['contents', project.id]) !== undefined) {
        queryClient.setQueryData(['contents', project.id], (oldData: any) => {
          return [...(oldData || []), result]
        })
      }
      queryClient.setQueryData(['content', project.id, result?.id], () => result)
      notify(
        {
          title: t('pages.configuration.contents.submit-success-title', { isUpdating: false }),
          message: t('pages.configuration.contents.submit-success-message', { isUpdating: false }),
          appearance: 'SUCCESS',
        },
        15000,
      )
      navigate(`/${project.id}/configuration/contents`)
    } catch (err: any) {
      if (err?.response?.status === CONFLICT_ERROR_CODE) {
        setError('name', {
          type: 'custom',
          message: t('pages.configuration.contents.content-name-already-exists'),
        })
      } else {
        notify(
          {
            title: t('pages.configuration.contents.submit-error', { isUpdating: false }),
            message: err,
            appearance: 'ERROR',
          },
          15000,
        )
      }
    } finally {
      setIsButtonLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="c-pt-4 c-pr-4 c-flex c-flex-col c-grow">
        <ContentForm contentType={contentType} isButtonLoading={isButtonLoading} />
      </form>
    </FormProvider>
  )
}

function UpdateContentForm({ content }: { content: TContent }) {
  const { t } = useTranslation()
  const methods = useForm<TUpdateContentFormValues>({
    defaultValues: {
      id: content.id,
      name: content.name,
      type: content.type,
      content: content.content,
    },
  })
  const { project } = useCurrentProject()
  const { handleSubmit } = methods
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const onSubmit = async (formData: TUpdateContentFormValues) => {
    setIsButtonLoading(true)
    try {
      const result = await ProjectService.patchSelfConfigurationContent(project.id, content.id, formData)
      if (queryClient.getQueryData(['contents', project.id]) !== undefined) {
        queryClient.setQueryData(['contents', project.id], (oldData: any) => {
          if (!oldData) return
          return [...oldData.filter((data: TContent) => data.id !== result.id), result]
        })
        queryClient.setQueryData(['content', project.id, content.id], () => result)
      }
      notify(
        {
          title: t('pages.configuration.contents.submit-success-title', { isUpdating: true }),
          message: t('pages.configuration.contents.submit-success-message', { isUpdating: true }),
          appearance: 'SUCCESS',
        },
        15000,
      )
      navigate(`/${project.id}/configuration/contents`)
    } catch (err: any) {
      notify(
        {
          title: t('pages.configuration.contents.submit-error', { isUpdating: true }),
          message: err,
          appearance: 'ERROR',
        },
        15000,
      )
    } finally {
      setIsButtonLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="c-pt-4 c-pr-4 c-flex c-flex-col c-grow">
        <ContentForm contentType={content.type} isButtonLoading={isButtonLoading} />
      </form>
    </FormProvider>
  )
}

export { AddContentForm, UpdateContentForm }
